import { AppId, AppTextFieldHandler } from "../../app/appTypes"
import { useAppDispatch, useAppSelector } from "../../app/hooks"
import { Alert, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Tab, TextField, Box, Typography } from "@mui/material"
import { Localized, useLocalization } from "@fluent/react"
import { If } from "../../app/If"
import { TabContext, TabList, TabPanel } from "@mui/lab"
import { showSuccess } from "../notifications/notificationsSlice"
import { useCallback, useState } from "react"
import { createNewCompanyMailTemplate, selectMailTemplateById, updateCompanyMailTemplate } from "./mailTemplatesSlice"
import { SaveButton } from "../../app/components/SaveButton"
import { CancelButton } from "../../app/components/CancelButton"


export interface MailTemplateDialogProps {
    id: AppId | undefined
    onSaveSuccess: () => void
    onCancel: () => void
}

export const MailTemplateDialog = ({ id,  onSaveSuccess, onCancel }: MailTemplateDialogProps) => {
    const dispatch = useAppDispatch()
    const { l10n } = useLocalization()
    
    const entity = useAppSelector(state => selectMailTemplateById(state, id ?? ""));
    const type= entity?.type ?? "";
    const context = entity?.context ?? "";
    const [body, setBody] = useState<string>(entity?.body ?? "");
    const [bodyEN, setBodyEN] = useState<string>(entity?.bodyEN ?? "");
    const [subject, setSubject] = useState<string>(entity?.subject ?? "");
    const [subjectEN, setSubjectEN] = useState<string>(entity?.subjectEN ?? "");
    const [saving, setSaving] = useState(false)
    const [serverError, setServerError] = useState<string | null>(null)
    const [reasonPromptOpen, setReasonPromptOpen] = useState(false)
    const [reason, setReason] = useState("")
    const [reasonError, setReasonError] = useState(false)
    const [tab, setTab] = useState<"polish" | "english">("polish");

    const isFixed = entity?.isFixed ?? false
    const description = l10n.getString(`mail-type-${type}-description`, null, "")

    const handleBodyChange: AppTextFieldHandler = useCallback(e => setBody(e.target.value), [setBody]);
    const handleBodyENChange: AppTextFieldHandler = useCallback(e => setBodyEN(e.target.value), [setBodyEN]);
    const handleSubjectChange: AppTextFieldHandler = useCallback(e => setSubject(e.target.value), [setSubject]);
    const handleSubjectENChange: AppTextFieldHandler = useCallback(e => setSubjectEN(e.target.value), [setSubjectEN]);

    const handleTabChange = useCallback((_, newTab) => {
        setTab(newTab)
    }, [])

    const handleSaveClick = () => {
        if (entity && !isFixed) {
            setReasonPromptOpen(true)
            setReasonError(false)
            setServerError(null)
        }
    }

    const handleConfirmSaveClick = async () => {
        if (!reason.trim()) {
            setReasonError(true)
            return
        }

        if(entity) {
            setServerError(null)
            setSaving(true)
            try {
                if (entity.companyId) {
                    let updatedTemplate = {
                        id: entity.id, type, body, subject, bodyEN, subjectEN, context, reason: reason.trim()
                    };

                    const result = await dispatch(updateCompanyMailTemplate(updatedTemplate))
                    if (updateCompanyMailTemplate.fulfilled.match(result)) {
                        dispatch(showSuccess("saved"))
                        onSaveSuccess()
                    } else {
                        let errorMsg = "error"
                        if (result.payload && result.payload.kind === 'http') {
                            const problem = result.payload.problem
                            if (problem) {
                                errorMsg = problem.title
        
                            }
                        }
                        setServerError(errorMsg)
                    }
                } else if(entity) {
                    let templatePayload = {
                        id: entity.id, type, body, subject, bodyEN, subjectEN, context, reason: reason.trim()
                    };

                    const result = await dispatch(createNewCompanyMailTemplate(templatePayload))
                    if (createNewCompanyMailTemplate.fulfilled.match(result)) {
                        dispatch(showSuccess("saved"))
                        onSaveSuccess()
                    } else {
                        let errorMsg = "error"
                        if (result.payload && result.payload.kind === 'http') {
                            const problem = result.payload.problem
                            if (problem) {
                                errorMsg = problem.title
        
                            }
                        }
                        setServerError(errorMsg)
                    }
                }
                
            }
            catch (error) {
                setServerError("error");
            }
            finally {
                setSaving(false)
            }
        }
    }

   return <>
        <DialogTitle>
            <If condition={id !== undefined} otherwise={
                <Localized id="new-mail-template">
                    <span>Nowy szablon</span>
                </Localized>
            }>
                <Localized id="edit-mail-template">
                    <span>Edycja szablonu</span>
                </Localized>
            </If>
        </DialogTitle>
        <DialogContent sx={{ pb: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <Grid container spacing={2} padding={2} sx={{ height: '100%', pb: 0, minHeight: 0 }}>
                
                {/* LEWA STRONA */}
                <Grid item xs={6} sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    minHeight: 0,
                    pt: '0 !important'
                }}>
                    <Box sx={{ flexShrink: 0, mb: 1, mt: 2 }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <If condition={isFixed}>
                                    <Alert severity="warning" sx={{ mb: 2 }}>
                                        <Localized id="mail-template-fixed-warning">
                                            <span>_Ten szablon jest wyłączony z edycji</span>
                                        </Localized>
                                    </Alert>
                                </If>
                            </Grid>
                            <Grid item xs={12} sm={6} sx={{ minWidth: 0 }}>
                                <Box sx={{
                                    minHeight: 40,
                                    px: 1.5,
                                    py: 0.75,
                                    border: 1,
                                    borderColor: 'divider',
                                    borderRadius: 1,
                                    backgroundColor: 'action.hover',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    minWidth: 0
                                }}>
                                    <Typography variant="caption" color="text.secondary" noWrap sx={{ flexShrink: 0 }}>
                                        <Localized id="mail-template-type">Typ</Localized>:
                                    </Typography>
                                    <Typography variant="body2" fontWeight={500} sx={{ minWidth: 0, overflowWrap: 'anywhere' }} title={type}>
                                        {type}
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12} sm={6} sx={{ minWidth: 0 }}>
                                <Box sx={{
                                    minHeight: 40,
                                    px: 1.5,
                                    py: 0.75,
                                    border: 1,
                                    borderColor: 'divider',
                                    borderRadius: 1,
                                    backgroundColor: 'action.hover',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    minWidth: 0
                                }}>
                                    <Typography variant="caption" color="text.secondary" noWrap sx={{ flexShrink: 0 }}>
                                        <Localized id="mail-template-name">_Nazwa</Localized>:
                                    </Typography>
                                    <Typography variant="body2" fontWeight={500} sx={{ minWidth: 0, overflowWrap: 'anywhere' }} title={l10n.getString(`mail-type-${type}`, null, `_${type}`)}>
                                        {l10n.getString(`mail-type-${type}`, null, `_${type}`)}
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12} sm={12} sx={{ order: { xs: 1, sm: 1 }, minWidth: 0 }}>
                                <Box sx={{
                                    minHeight: 40,
                                    px: 1.5,
                                    py: 0.75,
                                    border: 1,
                                    borderColor: 'divider',
                                    borderRadius: 1,
                                    backgroundColor: 'action.hover',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1
                                }}>
                                    <Typography variant="caption" color="text.secondary" noWrap sx={{ flexShrink: 0 }}>
                                        <Localized id="description">Opis</Localized>:
                                    </Typography>
                                    <Typography variant="body2" fontWeight={500} sx={{ minWidth: 0, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                                        {description || '-'}
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>
                    </Box>
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                        <TabContext value={tab}>
                            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                                <TabList onChange={handleTabChange}>
                                    <Tab
                                        key={0}
                                        value={"polish"}
                                        label={<Localized id="polish">_Polski</Localized>} />
                                    <Tab
                                        key={1}
                                        value={"english"}
                                        label={<Localized id="english">_Angielski</Localized>} />
                                </TabList>
                            </Box>
                            
                            <TabPanel value="polish" sx={{
                                flex: 1,
                                minHeight: 0,
                                display: tab === 'polish' ? 'flex' : 'none',
                                flexDirection: 'column',
                                p: 0,
                                pt: 2
                            }}>
                                <TextField
                                    fullWidth
                                    disabled={isFixed}
                                    value={subject}
                                    inputProps={{ maxLength: 100 }}
                                    onChange={handleSubjectChange}
                                    label={<Localized id="mail-subject">Temat PL</Localized>}
                                />
                                <TextField
                                    sx={{
                                        marginTop: 2,
                                        flex: 1,
                                        minHeight: 0,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        '& .MuiInputBase-root': {
                                            flex: 1,
                                            alignItems: 'flex-start',
                                            overflow: 'hidden'
                                        },
                                        '& textarea': {
                                            height: '100% !important',
                                            overflowY: 'auto !important'
                                        }
                                    }}
                                    fullWidth
                                    disabled={isFixed}
                                    value={body}
                                    multiline
                                    inputProps={{ maxLength: 10000 }}
                                    onChange={handleBodyChange}
                                    label={<Localized id="mail-body">Treść PL</Localized>}
                                />
                            </TabPanel>

                            <TabPanel value="english" sx={{
                                flex: 1,
                                minHeight: 0,
                                display: tab === 'english' ? 'flex' : 'none',
                                flexDirection: 'column',
                                p: 0,
                                pt: 2
                            }}>
                                <TextField
                                    fullWidth
                                    disabled={isFixed}
                                    value={subjectEN}
                                    inputProps={{ maxLength: 100 }}
                                    onChange={handleSubjectENChange}
                                    label={<Localized id="mail-subject-en">Temat EN</Localized>}
                                />
                                <TextField
                                    sx={{
                                        marginTop: 2,
                                        flex: 1,
                                        minHeight: 0,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        '& .MuiInputBase-root': {
                                            flex: 1,
                                            alignItems: 'flex-start',
                                            overflow: 'hidden'
                                        },
                                        '& textarea': {
                                            height: '100% !important',
                                            overflowY: 'auto !important'
                                        }
                                    }}
                                    fullWidth
                                    disabled={isFixed}
                                    value={bodyEN}
                                    multiline
                                    inputProps={{ maxLength: 10000 }}
                                    onChange={handleBodyENChange}
                                    label={<Localized id="mail-body-en">Treść EN</Localized>}
                                />
                            </TabPanel>
                        </TabContext>
                    </Box>
                </Grid>
                
                {/* PRAWA STRONA */}
                <Grid item xs={6} sx={{ overflow: 'auto', height: '100%', mt: 2 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}><Localized id="mail-preview-full">_Podgląd całego maila</Localized></Typography>
                    <Box sx={{ mb: 2 }}>
                        <strong><Localized id="mail-subject-label">_Temat:</Localized></strong> {tab === "polish" ? subject : subjectEN}
                    </Box>
                    <Box sx={{ p: 2, border: '1px solid #ccc', backgroundColor: '#f9f9f9', mb: 2 }}>
                        <div dangerouslySetInnerHTML={{ __html: (tab === "polish" ? body : bodyEN)/* + (selectedFooter ? selectedFooter.footer : '') */ }} />
                    </Box>
                </Grid>
            </Grid>

        </DialogContent>
        <DialogActions>
            <If condition={!isFixed}>
                <SaveButton onClick={handleSaveClick} loading={saving}/>
            </If>
            <CancelButton loading={saving} onClick={onCancel}/> 
        </DialogActions>
        <Dialog open={reasonPromptOpen} fullWidth maxWidth="xs">
            <DialogTitle>
                <Localized id="mail-change-reason-title">Powód zmiany szablonu</Localized>
            </DialogTitle>
            <DialogContent dividers>
                <TextField
                    fullWidth
                    autoFocus
                    required
                    multiline
                    minRows={2}
                    value={reason}
                    onChange={event => {
                        setReason(event.target.value)
                        if (reasonError && event.target.value.trim()) {
                            setReasonError(false)
                        }
                    }}
                    label={<Localized id="mail-change-reason-label">Powód zmiany</Localized>}
                    error={reasonError}
                    helperText={reasonError ? (
                        <Localized id="mail-change-reason-required">Podanie powodu jest wymagane.</Localized>
                    ) : (
                        <Localized id="mail-change-reason-helper">Opisz powód zmiany szablonu.</Localized>
                    )}
                />
                {serverError && (
                    <Alert sx={{ mt: 2 }} onClose={() => setServerError(null)} severity="error">
                        <Localized id={serverError}>
                            <span>Server error</span>
                        </Localized>
                    </Alert>
                )}
            </DialogContent>
            <DialogActions sx={{ p: 2, justifyContent: "flex-end" }}>
                <SaveButton loading={saving} onClick={handleConfirmSaveClick} />
                <CancelButton loading={saving} onClick={() => setReasonPromptOpen(false)} />
            </DialogActions>
        </Dialog>
    </>
}